import {
  Document,
  DocumentStatus,
  Folder,
  FolderChatStatus,
} from "~/api/model";
import { isDocumentBatesInFlight } from "../hooks/useBatesActions";

export const isDocumentSelectable = (
  document: Document,
  allFilesNotProcessed: boolean,
  caseData?: Folder
): boolean => {
  return (
    document.status === DocumentStatus.COMPLETE &&
    !isDocumentBatesInFlight(document) &&
    !allFilesNotProcessed &&
    caseData?.chatStatus === FolderChatStatus.COMPLETE
  );
};

export const getSelectableDocuments = (
  documents: Document[],
  allFilesNotProcessed: boolean,
  caseData?: Folder
): Document[] => {
  return documents.filter((doc) =>
    isDocumentSelectable(doc, allFilesNotProcessed, caseData)
  );
};

export const updateCaseInListProjects = (
  oldData: any,
  projectId: string,
  data: any
) => {
  return oldData.map((caseItem: any) =>
    caseItem.id === projectId ? { ...caseItem, ...data } : caseItem
  );
};

export const groupDocumentsByBatch = (documents: Document[]): Document[][] => {
  const batchGroups = new Map<string, Document[]>();

  documents.forEach((document) => {
    let batchKey: string;

    if (document.batchKey) {
      batchKey = document.batchKey;
    } else {
      // For legacy documents, group by authorId and hour
      const date = new Date(document.createdAt);
      const hourTimestamp = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours()
      ).getTime();

      batchKey = `legacy_${document.authorId}_${hourTimestamp}`;
    }

    if (!batchGroups.has(batchKey)) {
      batchGroups.set(batchKey, []);
    }
    batchGroups.get(batchKey)!.push(document);
  });

  return Array.from(batchGroups.values());
};

/**
 * Extracts the timestamp from a batchKey string.
 * Handles various formats like:
 * - "prefix_timestamp_userId" (normal uploads)
 * - "batch_import_timestamp_userId" (demo case imports)
 * - "legacy_authorId_timestamp" (legacy documents)
 *
 * @param batchKey - The batch key string to parse
 * @returns The timestamp as a number, or NaN if not found
 */
export const extractTimestampFromBatchKey = (batchKey: string): number => {
  const parts = batchKey.split("_");
  for (const part of parts) {
    const num = Number(part);
    if (!isNaN(num) && num > 1000000000000) {
      return num;
    }
  }
  return NaN;
};

export const parseBatchKey = (
  batchKey: string,
  locale: string = "en-US"
): { now: string; userId: string } => {
  const timestamp = extractTimestampFromBatchKey(batchKey);
  const parts = batchKey.split("_");
  const userId = parts[parts.length - 1] || "";
  return { now: formatBatchTimestamp(timestamp, locale), userId };
};

export const formatBatchTimestamp = (
  dateNumber: number,
  locale: string = "en-US"
): string => {
  if (isNaN(dateNumber)) {
    return "";
  }
  return new Date(dateNumber).toLocaleString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
